import { Check, Close } from "@mui/icons-material"
import DataGridComponent from "components/DataGridComponent/DataGridComponent"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { FileType, Norm, NormenImportInput } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useState } from "react"
import { AddNormsDialog } from "./AddNormsDialog"
import { ImportNormsDialog } from "./ImportNormsDialog"

const emptyNorm: Norm = {
  id: "NEW",
  name: "",
  revision: "",
  branch: "",
  documents: [],
  examinationCalculations: [],
  label: "",
  date: "",
  isOutdated: false,
  isHarmonized: false,
  isDeleted: false,
  deletedAt: "",
  tagCloud: [],
}

const AdminNorms = () => {
  const norms = useStoreState((state) => state.norms)
  const { updateNorm, deleteNorm, fetchNorms, importNormenFromExcel, setNormsDeleted } =
    useStoreActions((actions) => actions)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [formState, setFormState] = useState<Norm>(emptyNorm)
  const [openDelete, setOpenDelete] = useState(false)
  const [toDelete, setToDelete] = useState<string[]>([])
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [showDeleted, setShowDeleted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const refreshNorms = useCallback(
    async (options?: { fresh?: boolean }) => {
      try {
        await fetchNorms(options)
      } catch (error) {
        console.error("Fehler beim Laden der Normen:", error)
        enqueueSnackbar("Fehler beim Laden der Normen. Bitte erneut versuchen.", {
          variant: "error",
        })
      }
    },
    [fetchNorms]
  )

  const handleImportClick = () => setImportDialogOpen(true)

  const handleImportClose = () => setImportDialogOpen(false)

  const handleImportSubmit = async (input: NormenImportInput) => {
    setIsLoading(true)
    try {
      const result = await importNormenFromExcel(input)
      if (result.success) {
        await refreshNorms({ fresh: true })
      }
      return result
    } catch (error) {
      console.warn("Fehler beim Importieren der Normen:", error)
      return {
        success: false,
        message: (error as Error)?.message || "Unbekannter Fehler beim Import",
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refreshNorms()
  }, [refreshNorms])

  const handleEdit = (id: string) => {
    if (id === "NEW") {
      setFormState(emptyNorm)
    } else {
      const norm = norms.find((tb) => tb.id === id)
      if (norm) {
        setFormState(norm)
      }
    }
    setDialogOpen(true)
  }

  const handleDelete = (id: string[]) => {
    setOpenDelete(true)
    setToDelete(id)
  }

  const handleDeleteSelected = async () => {
    deleteNorm(toDelete)
    setToDelete([])
    setOpenDelete(false)
    await refreshNorms({ fresh: true })
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  const handleFormSubmit = async (arg: Norm, docs: { id: string; name: string }[]) => {
    const newNorm = {
      ...arg,
      documents: docs.map((item) => ({
        id: item.id,
        name: item.name,
        filename: "",
        fileURL: "",
        type: FileType.PDF,
        createdAt: new Date().toISOString(),
        createdBy: "",
        description: "",
      })),
    }
    updateNorm(newNorm)
    setDialogOpen(false)
    await refreshNorms()
  }

  const columns = [
    { field: "name", headerName: "Bezeichnung", flex: 1 },
    { field: "branch", headerName: "Branche", flex: 1 },
    { field: "label", headerName: "Titel", flex: 2 },
    { field: "date", headerName: "Jahr/Ausgabe", flex: 1 },
    { field: "revision", headerName: "Revision", flex: 2 },
    {
      field: "documents",
      headerName: "Dokumente",
      flex: 1,
      renderCell: (params: any) => {
        return <span>{params.value.length} Dokumente</span>
      },
    },
    {
      field: "isOutdated",
      headerName: "veraltet",
      flex: 1,
      renderCell: (params: any) => {
        return params.row.isOutdated ? <Check color="success" /> : <Close color="error" />
      },
    },
    {
      field: "isHarmonized",
      headerName: "harmonisiert",
      flex: 1,
      renderCell: (params: any) => {
        return params.row.isHarmonized ? <Check color="success" /> : <Close color="error" />
      },
    },
  ]

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={showDeleted} onChange={() => setShowDeleted((v) => !v)} />
          Gelöschte anzeigen
        </label>
      </div>
      <DataGridComponent
        columns={columns}
        rows={showDeleted ? norms : norms.filter((n) => !n.isDeleted)}
        getRowId={(row) => row.id}
        onEdit={handleEdit}
        onAdd={() => handleEdit("NEW")}
        onDelete={handleDelete}
        onRestore={async (id) => {
          await setNormsDeleted({ ids: [id], deleted: false })
          await refreshNorms({ fresh: true })
        }}
        isRowDeleted={(row) => !!row.isDeleted}
        importButton={true}
        importFunction={handleImportClick}
        noDelete={false}
      />

      <AddNormsDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        formState={formState}
        onSubmit={handleFormSubmit}
      />

      <DeleteDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onDelete={handleDeleteSelected}
      />
      <ImportNormsDialog
        open={importDialogOpen}
        onClose={handleImportClose}
        onImport={handleImportSubmit}
        isLoading={isLoading}
      />
    </>
  )
}

export default AdminNorms
